/**
 * Tasks Management Route (Story 28.2).
 *
 * Clones the Alerts page (routes/_authenticated/alerts/index.tsx) — same page
 * shell, table/card chrome, search, view switcher. Lists every task for the
 * tenant (general + per-client) with create / edit / delete + an INLINE,
 * OPTIMISTIC status change. Adds status/assignee/client/tags filters.
 *
 * State ownership (held in the PAGE):
 *  - Search: URL is source of truth (Route.validateSearch); searchInput +
 *    useDebounce(…, 300).
 *  - View mode: localStorage `taskList_view` (default "table").
 *  - Sort: derived `effectiveSort`, persisted to `taskList_sortBy`.
 *  - Filters (status/assignee/client/tags): page state; the
 *    inline-status mutation lives HERE (passed to the RQ-free views as
 *    `onStatusChange`).
 */

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import { Kanban, MagnifyingGlass } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { PageSpinner } from "../../../components/PageSpinner";
import { apiGet, apiPatch } from "../../../lib/apiClient";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { useUpgradeGuard } from "../../../hooks/useUpgradeGuard";
import { useDebounce } from "../../../hooks/useDebounce";
import { countTasksForGate } from "../../../lib/taskPlanGate";
import { TaskFilterToolbar } from "../../../components/tasks/TaskFilterToolbar";
import type { TaskViewMode } from "../../../components/tasks/ViewSwitcher";
import { TaskBoard } from "../../../components/tasks/TaskBoard";
import { TaskTableView } from "../../../components/tasks/TaskTableView";
import { TaskFormDialog } from "../../../components/tasks/TaskFormDialog";
import { DeleteTaskDialog } from "../../../components/tasks/DeleteTaskDialog";
import { distinctAssignees } from "../../../components/tasks/taskDisplay";
import { TASK_HIDEABLE_COLUMNS } from "../../../components/tasks/taskColumns";
import { useHiddenColumns } from "../../../components/shared/columnVisibility";
import type {
  Task,
  TaskTag,
  TaskStatus,
  SortOption,
} from "../../../components/tasks/types";

export interface TasksSearch {
  search?: string;
  sort?: SortOption;
  /** Comma-joined filter params (URL-synced — shareable, survive refresh). */
  status?: string;
  assignee?: string;
  client?: string;
  tags?: string;
}

function optionalStr(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

const VALID_SORTS: readonly SortOption[] = [
  "created_asc",
  "created_desc",
  "due_asc",
  "due_desc",
  "title_asc",
  "title_desc",
  "status_asc",
  "status_desc",
  "client_asc",
  "client_desc",
  "assignee_asc",
  "assignee_desc",
];

const STATUS_ORDER: Record<TaskStatus, number> = {
  todo: 0,
  in_progress: 1,
  done: 2,
};

/**
 * Locale comparator that keeps null values last in BOTH directions (so the
 * empty client / unassigned rows never lead). `dir` flips the non-null compare;
 * the null handling is direction-independent.
 */
function compareNullsLast(
  a: string | null,
  b: string | null,
  dir: "asc" | "desc",
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir === "asc" ? a.localeCompare(b) : b.localeCompare(a);
}

export const Route = createFileRoute("/_authenticated/tasks/")({
  validateSearch: (search: Record<string, unknown>): TasksSearch => ({
    search: typeof search.search === "string" ? search.search : undefined,
    sort: VALID_SORTS.includes(search.sort as SortOption)
      ? (search.sort as SortOption)
      : undefined,
    status: optionalStr(search.status),
    assignee: optionalStr(search.assignee),
    client: optionalStr(search.client),
    tags: optionalStr(search.tags),
  }),
  component: TasksPage,
});

interface ClientOption {
  id: string;
  name: string;
  demoMode: boolean;
}

interface AssigneeOption {
  id: string;
  name: string;
}

function TasksPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    search: urlSearch,
    sort: urlSort,
    status: urlStatus,
    assignee: urlAssignee,
    client: urlClient,
    tags: urlTags,
  } = Route.useSearch();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [lastEditingTask, setLastEditingTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [viewMode, setViewMode] = useState<TaskViewMode>(() => {
    // Table is the default. The key was bumped to `taskList_view` so a stale
    // earlier "board"/"grid" preference is ignored — everyone lands on the
    // table, while later toggles still persist under the new key.
    const stored = localStorage.getItem("taskList_view");
    return stored === "board" ? "board" : "table";
  });
  // Hidden table columns — per-device, persisted to localStorage. Shared by the
  // table (hide buttons) and the toolbar's Columns menu (restore).
  const { hiddenColumns, toggleColumn } = useHiddenColumns(
    "taskTable_hiddenColumns",
    TASK_HIDEABLE_COLUMNS,
  );
  // Filters are URL-synced (shareable, survive refresh + back/forward — AC #8):
  // derived from the URL, mutated via navigate.
  const statusFilter = useMemo<TaskStatus[]>(
    () =>
      (urlStatus ? urlStatus.split(",") : []).filter(
        (s): s is TaskStatus => s in STATUS_ORDER,
      ),
    [urlStatus],
  );
  const assigneeFilter = useMemo(
    () => (urlAssignee ? urlAssignee.split(",").filter(Boolean) : []),
    [urlAssignee],
  );
  const clientFilter = useMemo(
    () => (urlClient ? urlClient.split(",").filter(Boolean) : []),
    [urlClient],
  );
  const tagFilter = useMemo(
    () => (urlTags ? urlTags.split(",").filter(Boolean) : []),
    [urlTags],
  );
  const setFilterParam = useCallback(
    (key: "status" | "assignee" | "client" | "tags", values: string[]) => {
      navigate({
        from: Route.fullPath,
        search: (prev) => ({
          ...prev,
          [key]: values.length > 0 ? values.join(",") : undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );
  const setStatusFilter = useCallback(
    (s: TaskStatus[]) => setFilterParam("status", s),
    [setFilterParam],
  );
  const setAssigneeFilter = useCallback(
    (a: string[]) => setFilterParam("assignee", a),
    [setFilterParam],
  );
  const setClientFilter = useCallback(
    (c: string[]) => setFilterParam("client", c),
    [setFilterParam],
  );
  const setTagFilter = useCallback(
    (ids: string[]) => setFilterParam("tags", ids),
    [setFilterParam],
  );
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Actions.MANAGE_CLIENTS);

  // Toast state
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  function showToast(type: "success" | "error" | "info", message: string) {
    setToast({ type, message });
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }

  // ── Search state ──────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState(urlSearch ?? "");
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    setSearchInput(urlSearch ?? "");
  }, [urlSearch]);

  // ── Sort — derived state ──────────────────────────────────────────
  const effectiveSort: SortOption =
    urlSort ??
    (localStorage.getItem("taskList_sortBy") as SortOption | null) ??
    "created_desc";

  useEffect(() => {
    localStorage.setItem("taskList_sortBy", effectiveSort);
  }, [effectiveSort]);

  useEffect(() => {
    localStorage.setItem("taskList_view", viewMode);
  }, [viewMode]);

  // ── Linger the editing task so the dialog can play its exit animation ──
  useEffect(() => {
    if (editingTask) {
      setLastEditingTask(editingTask);
      return;
    }
    if (!lastEditingTask) return;
    const t = setTimeout(() => setLastEditingTask(null), 300);
    return () => clearTimeout(t);
  }, [editingTask, lastEditingTask]);

  // ── Tasks query ───────────────────────────────────────────────────
  const {
    data: tasks,
    isLoading,
    error,
  } = useQuery<Task[]>({
    queryKey: ["tasks"],
    queryFn: async () => {
      const res = await apiGet("/api/tasks");
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Failed to load tasks");
      }
      const json = await res.json();
      return json.data.tasks as Task[];
    },
  });

  // ── Clients query (create dialog + client filter + gate demo-exclusion). ──
  // This SHARES the ["clients"] react-query cache with the Clients page, so the
  // queryFn MUST return the FULL client objects unchanged. Stripping fields here
  // (the old `.map(...)`) overwrote that shared cache with a reduced shape and
  // made the Clients page render "Invalid Date" / blank columns until a hard
  // refetch. We cache the full rows and derive our smaller option list below.
  const { data: clientsData } = useQuery<ClientOption[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) return [];
      const json = await res.json();
      return json.data as ClientOption[];
    },
  });
  const clientOptions = useMemo<ClientOption[]>(
    () =>
      (clientsData ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        demoMode: !!c.demoMode,
      })),
    [clientsData],
  );

  // ── Team members query (assignee dropdown). Returns [] on a free-plan 403 —
  //    the owner can still create unassigned tasks. ──
  const { data: assigneeOptions } = useQuery<AssigneeOption[]>({
    queryKey: ["team", "members"],
    queryFn: async () => {
      const res = await apiGet("/api/team/members");
      if (!res.ok) return [];
      const json = await res.json();
      return (
        json.data as {
          userId: string;
          displayName: string | null;
          email: string;
        }[]
      ).map((m) => ({ id: m.userId, name: m.displayName ?? m.email }));
    },
  });

  // ── Tags query (shared tenant vocabulary — filter options + chips). ──
  const { data: allTags } = useQuery<TaskTag[]>({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await apiGet("/api/tags");
      if (!res.ok) return [];
      const json = await res.json();
      return (json.data as TaskTag[]).map((t) => ({
        id: t.id,
        name: t.name,
        color: t.color,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  // ── Filtering + sort pipeline ─────────────────────────────────────
  const filteredTasks = useMemo(() => {
    if (!tasks) return [];
    let result = [...tasks];

    // Status filter narrows to the chosen statuses; otherwise all tasks
    // (including done) are shown.
    if (statusFilter.length > 0) {
      result = result.filter((t) => statusFilter.includes(t.status));
    }

    if (assigneeFilter.length > 0) {
      result = result.filter(
        (t) => t.assignedTo !== null && assigneeFilter.includes(t.assignedTo),
      );
    }
    if (clientFilter.length > 0) {
      result = result.filter(
        (t) => t.clientId !== null && clientFilter.includes(t.clientId),
      );
    }
    if (tagFilter.length > 0) {
      result = result.filter((t) =>
        t.tags.some((tag) => tagFilter.includes(tag.id)),
      );
    }

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter((t) => t.title.toLowerCase().includes(q));
    }

    switch (effectiveSort) {
      case "created_asc":
        result.sort((a, b) => a.createdAt - b.createdAt);
        break;
      case "created_desc":
        result.sort((a, b) => b.createdAt - a.createdAt);
        break;
      case "due_asc":
        // Tasks with no due date sort last.
        result.sort((a, b) => (a.dueDate ?? Infinity) - (b.dueDate ?? Infinity));
        break;
      case "due_desc":
        result.sort(
          (a, b) => (b.dueDate ?? -Infinity) - (a.dueDate ?? -Infinity),
        );
        break;
      case "title_asc":
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "title_desc":
        result.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case "status_asc":
        result.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
        break;
      case "status_desc":
        result.sort((a, b) => STATUS_ORDER[b.status] - STATUS_ORDER[a.status]);
        break;
      case "client_asc":
        // Null client (renders "General") sorts last in both directions.
        result.sort((a, b) => compareNullsLast(a.clientName, b.clientName, "asc"));
        break;
      case "client_desc":
        result.sort((a, b) =>
          compareNullsLast(a.clientName, b.clientName, "desc"),
        );
        break;
      case "assignee_asc":
        // Null assignee (renders "Unassigned") sorts last in both directions.
        result.sort((a, b) =>
          compareNullsLast(a.assigneeName, b.assigneeName, "asc"),
        );
        break;
      case "assignee_desc":
        result.sort((a, b) =>
          compareNullsLast(a.assigneeName, b.assigneeName, "desc"),
        );
        break;
    }

    return result;
  }, [
    tasks,
    statusFilter,
    assigneeFilter,
    clientFilter,
    tagFilter,
    viewMode,
    debouncedSearch,
    effectiveSort,
  ]);

  // Filter option lists derived from loaded data.
  const assigneeFilterOptions = useMemo(
    () => distinctAssignees(tasks ?? []),
    [tasks],
  );
  const clientFilterOptions = useMemo(
    () => (clientOptions ?? []).map((c) => ({ id: c.id, name: c.name })),
    [clientOptions],
  );

  // Tenant-wide page count for the Create gate (taskLimit, 28.4). `undefined`
  // while queries load → guard refuses + the button disables.
  const pageTaskCount = countTasksForGate(tasks, clientOptions);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      navigate({
        from: Route.fullPath,
        search: (prev) => ({ ...prev, search: value || undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleSortChange = useCallback(
    (value: SortOption) => {
      navigate({
        from: Route.fullPath,
        search: (prev) => ({ ...prev, sort: value }),
      });
    },
    [navigate],
  );

  const clearAllFilters = useCallback(() => {
    setSearchInput("");
    navigate({
      from: Route.fullPath,
      search: (prev) => ({
        ...prev,
        search: undefined,
        status: undefined,
        assignee: undefined,
        client: undefined,
        tags: undefined,
      }),
    });
  }, [navigate]);

  const { guardAction, UpgradeModal } = useUpgradeGuard({
    feature: "taskLimit",
    currentCount: pageTaskCount,
  });

  function handleCreateClick() {
    guardAction(() => setCreateOpen(true));
  }

  // ── Inline optimistic status mutation (lives in the page; views are RQ-free) ──
  const updateStatus = useMutation({
    mutationFn: async ({ task, status }: { task: Task; status: TaskStatus }) => {
      const res = await apiPatch(`/api/tasks/${task.id}/status`, { status });
      const json = await res
        .json()
        .catch(() => ({ detail: "Server error. Please try again." }));
      if (!res.ok) throw { status: res.status, ...json };
      return json;
    },
    onMutate: async ({ task, status }) => {
      await queryClient.cancelQueries({ queryKey: ["tasks"] });
      const previous = queryClient.getQueryData<Task[]>(["tasks"]);
      queryClient.setQueryData<Task[]>(
        ["tasks"],
        (old) =>
          old?.map((t) => (t.id === task.id ? { ...t, status } : t)) ?? [],
      );
      return { previous };
    },
    onError: (err: unknown, _vars, context) => {
      const ctx = context as { previous?: Task[] } | undefined;
      if (ctx?.previous) queryClient.setQueryData(["tasks"], ctx.previous);
      const e = err as { title?: string; detail?: string };
      showToast("error", e?.title ?? e?.detail ?? "Failed to update task");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  function handleStatusChange(task: Task, status: TaskStatus) {
    updateStatus.mutate({ task, status });
  }

  function handleEditClick(task: Task) {
    setEditingTask(task);
  }

  const hasTasks = !!tasks && tasks.length > 0;

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Tasks" }]} />
      <div className="mb-6" />

      {!isLoading && !error && hasTasks && (
        <TaskFilterToolbar
          search={searchInput}
          onSearchChange={handleSearchChange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          assigneeFilter={assigneeFilter}
          onAssigneeFilterChange={setAssigneeFilter}
          clientFilter={clientFilter}
          onClientFilterChange={setClientFilter}
          tagFilter={tagFilter}
          onTagFilterChange={setTagFilter}
          assigneeOptions={assigneeFilterOptions}
          clientOptions={clientFilterOptions}
          tagOptions={allTags ?? []}
          canManage={canManage}
          onCreate={handleCreateClick}
          hiddenColumns={hiddenColumns}
          onToggleColumn={toggleColumn}
          showColumnsControl={viewMode === "table"}
        />
      )}

      {isLoading && <PageSpinner />}

      {error && (
        <div
          className="rounded-md border border-destructive bg-destructive/5 p-4 text-sm text-destructive"
          role="alert"
        >
          {error.message}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && tasks && tasks.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="rounded-full bg-primary/10 p-4 mb-4">
            <Kanban size={32} className="text-primary" />
          </div>
          <h2 className="text-lg font-semibold mb-2">No tasks yet</h2>
          <p className="text-muted-foreground mb-6 max-w-sm">
            Create a task to track client work and team deliverables.
          </p>
          {canManage && (
            <Button
              variant="brand"
              className="rounded-2xl text-[13px] cursor-pointer"
              onClick={handleCreateClick}
            >
              <Kanban size={16} className="mr-2" />
              Create your first task
            </Button>
          )}
        </div>
      )}

      {/* No-results state */}
      {!isLoading && !error && hasTasks && filteredTasks.length === 0 && (
        <div className="text-center py-12">
          <MagnifyingGlass
            size={48}
            className="mx-auto text-muted-foreground mb-4"
          />
          <h3 className="text-lg font-medium">No tasks match your filters</h3>
          <p className="mt-1" style={{ color: "#5A7396" }}>
            Try adjusting your search or filters.
          </p>
          <Button
            variant="outline"
            onClick={clearAllFilters}
            className="mt-4 rounded-2xl border-[0.5px] border-input text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
          >
            Clear all filters
          </Button>
        </div>
      )}

      {/* Body */}
      {!isLoading && !error && hasTasks && filteredTasks.length > 0 && (
        <AnimatePresence mode="wait">
          {viewMode === "board" ? (
            <motion.div
              key="board"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <TaskBoard
                tasks={filteredTasks}
                canManage={canManage}
                onTaskClick={canManage ? handleEditClick : undefined}
                onEditClick={handleEditClick}
                onDeleteClick={(task) => setDeleteTarget(task)}
                onStatusChange={handleStatusChange}
              />
            </motion.div>
          ) : (
            <motion.div
              key="table"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <TaskTableView
                tasks={filteredTasks}
                canManage={canManage}
                onTaskClick={canManage ? handleEditClick : undefined}
                onEditClick={handleEditClick}
                onDeleteClick={(task) => setDeleteTarget(task)}
                onStatusChange={handleStatusChange}
                triggerRefs={triggerRefs}
                sortBy={effectiveSort}
                onSortChange={handleSortChange}
                hiddenColumns={hiddenColumns}
                onHideColumn={toggleColumn}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Create dialog */}
      {canManage && (
        <TaskFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          clients={clientOptions ?? []}
          assignees={assigneeOptions ?? []}
          existingTasks={tasks ?? []}
          onToast={showToast}
          onSaved={() => showToast("success", "Task created")}
        />
      )}

      {/* Edit dialog — lingering lastValue so AnimatePresence can exit */}
      {canManage && lastEditingTask && (
        <TaskFormDialog
          open={editingTask !== null}
          onOpenChange={(o) => !o && setEditingTask(null)}
          clients={clientOptions ?? []}
          assignees={assigneeOptions ?? []}
          task={lastEditingTask}
          existingTasks={tasks ?? []}
          onToast={showToast}
          onSaved={() => showToast("success", "Task updated")}
        />
      )}

      {/* Delete confirmation dialog */}
      <DeleteTaskDialog
        task={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          showToast("success", "Task deleted");
        }}
        showToast={showToast}
      />

      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 rounded-md px-4 py-3 text-sm shadow-lg ${
            toast.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : toast.type === "info"
                ? "bg-blue-50 border border-blue-200 text-blue-800"
                : "bg-destructive/10 border border-destructive/20 text-destructive"
          }`}
          role={toast.type === "error" ? "alert" : "status"}
        >
          {toast.message}
        </div>
      )}

      {UpgradeModal}
    </div>
  );
}
